import Cookies from 'js-cookie';
import merge from 'lodash.merge';
import { useSearchParams } from 'next/navigation';
import { useCallback } from 'react';

import { AddError, useLogger } from '@/analytics';
import { cookieOptions } from '@/helpers/cookies';
import { VISITOR_KEY, SESSION_KEY, VID_KEY } from '@/helpers/meta';
import { toVisitorCookie, Visitor, VisitorCookie } from '@/models';

const tryParseCookie = <T>(name: string, addError: AddError): T | undefined => {
  const json = Cookies.get(name);

  if (!json) {
    return;
  }

  try {
    return JSON.parse(json);
  } catch (err) {
    addError(err, { action: 'tryParseCookie', component: 'useVisitorCookie' });
  }
};

export const useVisitorCookie = () => {
  const { addError } = useLogger();

  const setVisitorCookie = useCallback(
    (updates: Partial<Visitor>) => {
      if (Object.keys(updates).length === 0) {
        return;
      }

      const current = tryParseCookie<VisitorCookie>(VISITOR_KEY, addError);
      const newCookie = toVisitorCookie(merge({}, current, updates));

      Cookies.set(VISITOR_KEY, JSON.stringify(newCookie), cookieOptions(window.location.hostname));
    },
    [addError]
  );

  const searchParams = useSearchParams();

  const resetCookies = useCallback(() => {
    if (searchParams.get('reload')) {
      // eslint-disable-next-line no-console
      console.error('Multiple clear visitor and reloads');
      return;
    }

    const { hostname } = window.location;

    clearCookies(hostname);

    const url = new URL(window.location.href);
    url.searchParams.set('reload', 'true');
    window.location.assign(url.toString());
  }, [searchParams]);

  return { resetCookies, setVisitorCookie };
};

const clearCookies = (hostname: string) => {
  const options = cookieOptions(hostname);

  Cookies.remove(VID_KEY, options);
  Cookies.remove(SESSION_KEY, options);
  Cookies.remove(VISITOR_KEY, options);
};
